/**
 * Layer Admin Explorer
 *
 * Administrative interface for managing map layers in the database.
 * Part of Layer Drawer 2.0 - Phase 3 (Admin UI).
 *
 * Features:
 * - CRUD operations for layers
 * - GeoServer import functionality
 * - Layer preview and testing
 * - Global preset management
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useFusionAuth } from '@fusionauth/react-sdk';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFileImport, faFileArrowUp, faFileArrowDown, faGear, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import ConfigurableBaseExplorer from '../baseExplorer/ConfigurableBaseExplorer';
import { layerAdminExplorerConfig, translateParams, buildExportableLayers } from './LayerAdminExplorerConfig';
import { Layer } from '../../../types/layer';
import { CreateLayerModal } from './modals/CreateLayerModal';
import { ImportGeoServerModal } from './modals/ImportGeoServerModal';
import { ImportBulkModal } from './modals/ImportBulkModal';
import { PresetManagerModal } from './modals/PresetManagerModal';
import { BulkEditModal } from './modals/BulkEditModal';
import { useLayerDrawer } from '../../../easymap/layerDrawer/LayerDrawerContext';
import { LayerService } from '../../../services/layerService';
import type { LayerFilters } from '../../../types/layer';
import './LayerAdminExplorer.scss';
import { useMessages } from '../../common/AppMessages';

interface LayerAdminExplorerProps {
  className?: string;
  initialPosition?: { x: number; y: number };
  initialSize?: { width: number; height: number };
  onClose?: () => void;
  onSelectionChange?: (selection: Layer[]) => void;
  onDataChange?: () => void;
  isWindowed?: boolean;
  windowTitle?: string;
  resizable?: boolean;
  draggable?: boolean;
  /** If provided, opens edit modal for this layer on mount */
  initialLayerId?: string;
}

/**
 * Layer Admin Explorer Component
 *
 * Provides administrative interface for layer management.
 * Uses ConfigurableBaseExplorer pattern similar to WeatherStationExplorer and WildfireExplorer.
 */
const LayerAdminExplorer: React.FC<LayerAdminExplorerProps> = (props) => {
  const { showMessage } = useMessages();
  const { isLoggedIn } = useFusionAuth();
  const { refreshLayers } = useLayerDrawer();

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState<Layer | null>(null);

  // Refresh key for forcing data reload
  const [refreshKey, setRefreshKey] = useState(0);

  // Track the most recent filters/search the explorer fetched with so the
  // Export button can reproduce the same filtered set (without pagination).
  const lastFetchParamsRef = useRef<any>(null);

  // Mobile-aware default sizing
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const defaultPosition = isMobile
    ? { x: 5, y: 5 }
    : { x: 100, y: 100 };
  const defaultSize = isMobile
    ? { width: window.innerWidth - 10, height: window.innerHeight - 10 }
    : { width: 1400, height: 800 };

  // Default props
  const {
    className,
    initialPosition = defaultPosition,
    initialSize = defaultSize,
    onClose,
    onSelectionChange,
    onDataChange,
    isWindowed = true,
    windowTitle = 'Layer Administration',
    resizable = true,
    draggable = true,
    initialLayerId,
  } = props;

  /**
   * Handle initialLayerId - fetch the layer and open edit modal
   */
  useEffect(() => {
    if (initialLayerId) {
      // Fetch the layer and open edit modal
      fetch(`/api/layers/${initialLayerId}`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.data) {
            setSelectedLayer(data.data);
            setShowCreateModal(true);
          }
        })
        .catch(err => {
          console.error('[LayerAdminExplorer] Failed to fetch layer for editing:', err);
        });
    }
  }, [initialLayerId]);

  /**
   * Handle entity edit - opens edit modal with selected layer
   */
  const handleEntityEdit = (layer: Layer) => {
    console.log('[LayerAdminExplorer] Edit layer:', layer);
    setSelectedLayer(layer);
    setShowCreateModal(true);
  };

  /**
   * Handle entity selection
   */
  const handleEntitySelect = (layer: Layer) => {
    console.log('[LayerAdminExplorer] Layer selected:', layer);
    if (onSelectionChange) {
      onSelectionChange([layer]);
    }
  };

  /**
   * Handle data changes - refresh explorer
   */
  const handleDataRefresh = () => {
    console.log('[LayerAdminExplorer] Refreshing data');
    setRefreshKey((prev) => prev + 1);
    if (onDataChange) {
      onDataChange();
    }
  };

  /**
   * Handle create layer button click
   */
  const handleCreateClick = () => {
    setSelectedLayer(null);
    setShowCreateModal(true);
  };

  /**
   * Handle import from GeoServer button click
   */
  const handleImportClick = () => {
    setShowImportModal(true);
  };

  /**
   * Handle bulk import button click
   */
  const handleBulkImportClick = () => {
    setShowBulkImportModal(true);
  };

  /**
   * Handle bulk edit button click — opens the modal that mass-applies
   * enabled / default_opacity changes across the currently-filtered set.
   */
  const handleBulkEditClick = () => {
    setShowBulkEditModal(true);
  };

  /**
   * Handle preset manager button click
   */
  const handlePresetClick = () => {
    setShowPresetModal(true);
  };

  /**
   * Wrap the config's serviceAdapter so we can record the QueryParams the
   * explorer fetches with. Export uses these to reproduce the same filtered
   * set without pagination.
   */
  const wrappedConfig = useMemo(() => {
    const original = layerAdminExplorerConfig.serviceAdapter;
    return {
      ...layerAdminExplorerConfig,
      serviceAdapter: {
        ...original,
        fetchData: async (params: any) => {
          lastFetchParamsRef.current = params;
          return original.fetchData(params);
        },
      },
    };
  }, []);

  /**
   * Build the LayerFilters that match the explorer's current view, used by
   * both Export and Bulk Edit. Delegates to the shared translateParams from
   * LayerAdminExplorerConfig so the boolean-coercion and field-routing
   * logic only lives in one place — a duplicate copy here previously fell
   * out of sync, causing Bulk Edit to ignore the enabled filter and target
   * every layer in the DB instead of the filtered set.
   */
  const buildExportFilters = useCallback((): LayerFilters => {
    return translateParams(lastFetchParamsRef.current ?? {});
  }, []);

  /**
   * Handle export button click. Re-queries with the explorer's current
   * filters/search (no pagination) and downloads the result as JSON.
   */
  const handleExportClick = async () => {
    try {
      const layerService = new LayerService();
      const layers = await layerService.getAllLayers(buildExportFilters());

      // #693: apply the SAME client-side column filters the table applies (and
      // strip DB-managed fields) so the export matches the narrowed view instead
      // of dumping every backend row.
      const exportable = buildExportableLayers(layers, lastFetchParamsRef.current ?? {});

      const blob = new Blob([JSON.stringify(exportable, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `easymap-layers-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[LayerAdminExplorer] Export failed:', err);
      showMessage('Export failed — see console for details.', 'error');
    }
  };

  /**
   * Handle create/edit modal close
   */
  const handleCreateModalClose = async (success: boolean) => {
    setShowCreateModal(false);
    setSelectedLayer(null);

    if (success) {
      handleDataRefresh();
      // Also refresh LayerDrawer to pick up config changes
      await refreshLayers();
    }
  };

  /**
   * Handle import modal close
   */
  const handleImportModalClose = async (success: boolean) => {
    setShowImportModal(false);

    if (success) {
      handleDataRefresh();
      // Also refresh LayerDrawer to pick up new layers
      await refreshLayers();
    }
  };

  /**
   * Handle bulk import modal close
   */
  const handleBulkImportModalClose = async (success: boolean) => {
    setShowBulkImportModal(false);

    if (success) {
      handleDataRefresh();
      // Also refresh LayerDrawer to pick up new layers
      await refreshLayers();
    }
  };

  /**
   * Handle bulk edit modal close — refresh table + LayerDrawer on success
   * so enabled/opacity changes propagate to the live map immediately.
   */
  const handleBulkEditModalClose = async (success: boolean) => {
    setShowBulkEditModal(false);

    if (success) {
      handleDataRefresh();
      await refreshLayers();
    }
  };

  /**
   * Handle preset modal close
   */
  const handlePresetModalClose = () => {
    setShowPresetModal(false);
  };

  return (
    <div className={`layer-admin-explorer ${className || ''}`}>
      <ConfigurableBaseExplorer<any>
        refreshTrigger={refreshKey}
        config={wrappedConfig}
        isAuthenticated={isLoggedIn}
        onEntityEdit={handleEntityEdit}
        onEntitySelect={handleEntitySelect}
        onEntityCreate={handleDataRefresh}
        isWindowed={isWindowed}
        windowTitle={windowTitle}
        initialPosition={initialPosition}
        initialSize={initialSize}
        resizable={resizable}
        draggable={draggable}
        showCloseButton={true}
        showMinimizeButton={true}
        onWindowClose={onClose}
        className="layer-admin-explorer-configured"
        customToolbar={
          <>
            <button
              className="btn-primary"
              onClick={handleCreateClick}
              title="Add a new layer"
              aria-label="Add a new layer"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span className="btn-label">Add New Layer</span>
            </button>
            <button
              className="btn-secondary"
              onClick={handleImportClick}
              title="Import layers from GeoServer"
              aria-label="Import layers from GeoServer"
            >
              <FontAwesomeIcon icon={faFileImport} />
              <span className="btn-label">Import from GeoServer</span>
            </button>
            <button
              className="btn-secondary"
              onClick={handleBulkImportClick}
              title="Bulk import layers from JSON"
              aria-label="Bulk import layers from JSON"
            >
              <FontAwesomeIcon icon={faFileArrowUp} />
              <span className="btn-label">Bulk Import</span>
            </button>
            <button
              className="btn-secondary"
              onClick={handleBulkEditClick}
              title="Bulk-edit enabled/opacity for the currently filtered layers"
              aria-label="Bulk edit currently filtered layers"
            >
              <FontAwesomeIcon icon={faPenToSquare} />
              <span className="btn-label">Bulk Edit</span>
            </button>
            <button
              className="btn-secondary"
              onClick={handleExportClick}
              title="Export currently filtered layers as JSON"
              aria-label="Export currently filtered layers as JSON"
            >
              <FontAwesomeIcon icon={faFileArrowDown} />
              <span className="btn-label">Export</span>
            </button>
            <button
              className="btn-secondary"
              onClick={handlePresetClick}
              title="Manage layer presets"
              aria-label="Manage layer presets"
            >
              <FontAwesomeIcon icon={faGear} />
              <span className="btn-label">Manage Presets</span>
            </button>
          </>
        }
      />

      {/* Create/Edit Layer Modal */}
      {showCreateModal &&
        createPortal(
          <CreateLayerModal
            layer={selectedLayer}
            onClose={handleCreateModalClose}
          />,
          document.body,
        )}

      {/* Import from GeoServer Modal */}
      {showImportModal &&
        createPortal(
          <ImportGeoServerModal onClose={handleImportModalClose} />,
          document.body,
        )}

      {/* Bulk Import Modal */}
      {showBulkImportModal &&
        createPortal(
          <ImportBulkModal onClose={handleBulkImportModalClose} />,
          document.body,
        )}

      {/* Bulk Edit Modal */}
      {showBulkEditModal &&
        createPortal(
          <BulkEditModal
            onClose={handleBulkEditModalClose}
            getFilters={buildExportFilters}
          />,
          document.body,
        )}

      {/* Preset Manager Modal */}
      {showPresetModal &&
        createPortal(
          <PresetManagerModal onClose={handlePresetModalClose} />,
          document.body,
        )}
    </div>
  );
};

export default LayerAdminExplorer;
